import React, { useState } from 'react';
import { Moment } from 'moment';
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
} from 'antd';
// Types
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketTierFields } from 'api/ticketing/types/TicketTierFields';
// Hooks
import { useWithSalesPaused } from 'pages/Events/hooks/useWithSalesPaused';
import { useTicketEventChanges } from 'pages/Events/hooks/useTicketEventChanges';
// Helpers
import {
  formatMoney,
  getAddTierAvailability,
  getTicketingErrorMessage,
  toVenueInstant,
  validateForm,
  venueDateTimePickerProps,
} from 'pages/Events/helpers';
import {
  buildTierInput,
  DEFAULT_MAX_PER_ORDER,
  fitMaxAdmissions,
  getTierCapacityTotal,
} from 'pages/Events/components/TicketEventForm/helpers';
// Constants
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './AddTierModal.module.scss';

type AddTierFormValues = {
  label: string;
  price: number;
  capacity: number;
  maxPerOrder?: number;
  description?: string;
  salesOpenAt?: Moment | null;
  salesCloseAt?: Moment | null;
};

type AddTierModalProps = {
  event: TicketEventDetailsFields;
  tiers: TicketTierFields[];
  minTicketPrice: number;
  maxTicketPrice: number;
  visible: boolean;
  onClose: () => void;
  onAdded: () => void;
};

const AddTierModal = ({
  event,
  tiers,
  minTicketPrice,
  maxTicketPrice,
  visible,
  onClose,
  onAdded,
}: AddTierModalProps): JSX.Element => {
  const [form] = Form.useForm<AddTierFormValues>();
  const [saving, setSaving] = useState(false);
  const withSalesPaused = useWithSalesPaused();
  const { updateDetails, addTier } = useTicketEventChanges();
  const { pausesSales } = getAddTierAvailability(event.status);

  const handleOk = async (): Promise<void> => {
    const values = await validateForm(form);

    if (!values) {
      return;
    }
    const maxAdmissions = fitMaxAdmissions(
      event.maxAdmissions,
      getTierCapacityTotal(tiers) + Number(values.capacity || 0)
    );
    let current = event;

    setSaving(true);

    try {
      if (maxAdmissions !== event.maxAdmissions) {
        current =
          (await updateDetails({
            ticketEventId: event.id,
            expectedVersion: event.version,
            maxAdmissions,
          })) || current;
      }

      await withSalesPaused(current, () =>
        addTier({
          ...buildTierInput(values),
          ticketEventId: event.id,
          salesOpenAt: values.salesOpenAt
            ? toVenueInstant(values.salesOpenAt, event.timezone)
            : null,
          salesCloseAt: values.salesCloseAt
            ? toVenueInstant(values.salesCloseAt, event.timezone)
            : null,
        })
      );

      successNotification(`Tier "${values.label.trim()}" added`);
      form.resetFields();
      onAdded();
    } catch (error) {
      errorNotification(
        getTicketingErrorMessage(error, 'Failed to add the tier')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add ticket tier"
      visible={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="Add tier"
      confirmLoading={saving}
      destroyOnClose
      width={640}
    >
      {pausesSales && (
        <Alert
          type="info"
          showIcon
          message="Sales pause for a moment while the tier is added, then reopen."
          className={styles.alert}
        />
      )}
      <Form<AddTierFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ maxPerOrder: DEFAULT_MAX_PER_ORDER }}
      >
        <Form.Item
          name="label"
          label="Name"
          rules={[
            { required: true, whitespace: true, message: 'Name the tier' },
            { max: 120, message: 'At most 120 characters' },
          ]}
        >
          <Input placeholder="e.g. VIP" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="price"
              label="Price ($)"
              rules={[
                { required: true, message: 'Set a price' },
                {
                  type: 'number',
                  min: minTicketPrice,
                  max: maxTicketPrice,
                  message: `Between ${formatMoney(
                    minTicketPrice
                  )} and ${formatMoney(maxTicketPrice)}`,
                },
              ]}
            >
              <InputNumber min={0} precision={2} className={styles.fullWidth} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="capacity"
              label="Quantity"
              rules={[
                { required: true, message: 'Set a quantity' },
                {
                  type: 'integer',
                  min: 1,
                  max: 1000000,
                  message: 'A whole number from 1 to 1,000,000',
                },
              ]}
            >
              <InputNumber min={1} precision={0} className={styles.fullWidth} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="maxPerOrder"
              label="Max per order"
              rules={[
                { type: 'integer', min: 1, max: 100, message: 'From 1 to 100' },
              ]}
            >
              <InputNumber
                min={1}
                max={100}
                precision={0}
                className={styles.fullWidth}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="description" label="Description">
          <Input placeholder="Optional: what this tier includes" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="salesOpenAt"
              label="Tier sales open"
              extra="Optional. Defaults to the event's sales window."
            >
              <DatePicker
                {...venueDateTimePickerProps(event.timezone)}
                className={styles.fullWidth}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="salesCloseAt"
              label="Tier sales close"
              dependencies={['salesOpenAt']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value?: Moment | null) => {
                    const open: Moment | undefined =
                      getFieldValue('salesOpenAt');

                    return !value || !open || value.isAfter(open)
                      ? Promise.resolve()
                      : Promise.reject(new Error('Must be after sales open'));
                  },
                }),
              ]}
            >
              <DatePicker
                {...venueDateTimePickerProps(event.timezone)}
                className={styles.fullWidth}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddTierModal;
